// Recipient (payee) tax profile used to issue Form 1099-NEC for money paid to a
// referee through PANDA Pay.
//
// Payer boxes (payer name/address/TIN, account number) are NOT collected from
// the recipient: PARC Sports LLC is always the payer. They come from
// tax_payer_profile on the statement response — see irs_payer.
//
// Box amounts are NOT user-editable either. Box 1a is derived from the wallet
// ledger and surfaced read-only, so the platform can always substantiate what
// it reports.
//
// Lives under services/ rather than with the Settings tab because two features
// share it: the mandatory onboarding step in the app shell, and Settings > 1099.

export type TinType = 'ssn' | 'ein' | 'itin';

export type IrsProfileStatus = 'incomplete' | 'certified';

/** Shape returned by GET tax_form?type=get_recipient_profile */
export interface irs_recipient_profile {
  id?: string;

  // Taxpayer identification
  recipient_type?: string;
  legal_first_name?: string;
  legal_middle_name?: string;
  legal_last_name?: string;
  tin_type?: TinType;
  /**
   * Only the last four digits are ever returned by the API — the full TIN is
   * write-only. An empty value means no TIN is on file yet.
   */
  tin_last_four?: string;

  // Mailing address (the recipient block of the form)
  /** Address Line 1 — prints in the form's "Street Address" cell. */
  street_address?: string;
  /** Address Line 2 — prints in the form's "Apt. No." cell. */
  apt_suite?: string;
  city?: string;
  state?: string;
  postal_code?: string;

  // Certification, signed once at onboarding
  certification_name?: string;
  certified_at?: string;
  status?: IrsProfileStatus;
  updated_at?: string;
}

/** Body sent to PATCH tax_form?type=update_recipient_profile */
export interface set_irs_recipient_profile {
  recipient_type: string;
  legal_first_name: string;
  legal_middle_name?: string;
  legal_last_name: string;
  tin_type: TinType;
  /** Digits only, no separators. Omitted when the user did not re-enter it. */
  tin?: string;

  street_address: string;
  apt_suite?: string;
  city: string;
  state: string;
  postal_code: string;

  /**
   * The penalties-of-perjury declaration. Sent on the first save only: that is
   * the one the user signs. A later edit from Settings changes an address, not
   * a declaration, and omitting these preserves the original signature.
   */
  certifies_tin_correct?: boolean;
  certifies_us_person?: boolean;
  certification_name?: string;
}

/**
 * The recipient block as it appears ON THE FORM. This is the one shape that
 * carries the full taxpayer ID: Copy B is the recipient's own statement and the
 * number on it is their own.
 *
 * Comes only from GET tax_form?type=get_statement, which is fetched when the
 * user opens the review or downloads the PDF — not on page load. Hold it for as
 * long as the form is on screen and drop it after; do not push it into a
 * BehaviorSubject or a cache, and never log it.
 */
export interface irs_statement_recipient extends irs_recipient_profile {
  /** Digits only, unmasked. Null when no TIN is stored. */
  tin?: string;
}

export interface irs_payer {
  name?: string;
  street_address?: string;
  room_or_suite?: string;
  city_or_town?: string;
  state_or_province?: string;
  country?: string;
  postal_code?: string;
  telephone?: string;
  tin?: string;
  state_number?: string;
}

/** Shape returned by GET tax_form?type=get_statement */
export interface irs_statement {
  recipient?: irs_statement_recipient;
  amounts?: irs_reported_amounts;
  payer?: irs_payer;
}

/**
 * Read-only amounts for the selected tax year, calculated by the API from the
 * wallet ledger: the money other users paid into this user's wallet, which for
 * a referee is what coaches and registrars paid them. Money the user deposited
 * themselves is not income and is not included.
 */
export interface irs_reported_amounts {
  tax_year?: number;
  /** Box 1a — nonemployee compensation received in the year */
  nonemployee_compensation?: number;
  /** Box 4 — zero until backup withholding is implemented */
  federal_tax_withheld?: number;
  /** Number of payments making up nonemployee_compensation. */
  payments_received?: number;
  /** issued | pending | not_required */
  filing_status?: string;
}

export interface option {
  value: string;
  label: string;
}

export const NAME_TYPES: option[] = [
  { value: 'individual', label: 'Individual' },
];

/** Two-letter codes are what the IRS expects in the state boxes. */
export const US_STATES: option[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'AS', label: 'American Samoa' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'GU', label: 'Guam' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'VI', label: 'U.S. Virgin Islands' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

/** The roles the tax profile is mandatory for; both are paid through PANDA Pay. */
export const TAX_PROFILE_REQUIRED_ROLES = ['referee', 'assigner'];

/**
 * Groups a nine-digit TIN the way the IRS prints it: EINs as xx-xxxxxxx,
 * SSNs and ITINs as xxx-xx-xxxx.
 *
 * Shared by the on-screen replica and the PDF so the two cannot format the same
 * number differently. Anything that is not nine digits is returned as-is rather
 * than forced into a layout it does not fit.
 */
export function formatTin(tin: string, tin_type: string): string {
  const digits = (tin ?? '').replace(/\D/g, '');

  if (digits.length !== 9) {
    return tin ?? '';
  }

  return tin_type === 'ein'
    ? `${digits.slice(0, 2)}-${digits.slice(2)}`
    : `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}
